import { useMemo } from 'react';
import type { ReactNode } from 'react';

import {
  type DataTableExternalSelectionAdapter,
  type DataTableFeatures,
  getSelectionColumn
} from 'design-system';

import type { CellContext, ColumnDef } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { useDateText } from '@/features/_shared/user';

import { CatalogCellContent } from '../components/CatalogTable/CatalogCellContent';
import { forwardCurvesColumns, spotColumns, SYMBOL_COLUMN_WIDTH } from '../constants';

type CatalogColumnDef = ColumnDef<DataTableFeatures, CatalogItem>;

export interface UseCatalogColumnsOptions {
  isForwardCurves: boolean;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  selectionAdapter: DataTableExternalSelectionAdapter<CatalogItem>;
}

function getColumnDefinitionId(column: CatalogColumnDef): string | undefined {
  if ('id' in column && column.id) return column.id;
  if ('accessorKey' in column) return String(column.accessorKey);
  return undefined;
}

export function useCatalogColumns({
  isForwardCurves,
  isInDashboard,
  renderFactsheetButton,
  selectionAdapter
}: UseCatalogColumnsOptions): CatalogColumnDef[] {
  const { formatDateString } = useDateText();
  const baseColumns = (isForwardCurves ? forwardCurvesColumns : spotColumns) as CatalogColumnDef[];

  return useMemo(
    () => [
      getSelectionColumn<CatalogItem>({
        size: SYMBOL_COLUMN_WIDTH,
        selection: { type: 'external', adapter: selectionAdapter }
      }),
      ...baseColumns
        .filter(column => getColumnDefinitionId(column) !== 'Symbol')
        .map(column => ({
          ...column,
          cell: (context: CellContext<DataTableFeatures, CatalogItem>) => (
            <CatalogCellContent
              context={context}
              formatDateString={formatDateString}
              isInDashboard={isInDashboard}
              isRestricted={selectionAdapter.getRowState(context.row).disabled}
              renderFactsheetButton={renderFactsheetButton}
            />
          )
        }))
    ],
    [baseColumns, formatDateString, isInDashboard, renderFactsheetButton, selectionAdapter]
  );
}
